/**
 * Presence Manager Store
 * Zustand store for managing PresenceManager state with encrypted persistence
 * Follows the preferencesStore pattern with encrypted localStorage
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import CryptoJS from 'crypto-js';
import {
  PresenceManager,
  PresenceStatus,
  UserPresenceUpdate,
  PresenceStats,
  ConnectionState,
} from '@actschurch/ccms-sdk';

const ENCRYPTION_KEY = import.meta.env.VITE_STORAGE_ENCRYPTION_KEY || 'ccms-presence-key';

// Module-level interval ID for cleanup (prevents memory leak)
let countdownIntervalId: ReturnType<typeof setInterval> | null = null;

// Encrypted storage (matching preferencesStore pattern)
const encryptedStorage = {
  getItem: (name: string) => {
    const encrypted = localStorage.getItem(name);
    if (!encrypted) return null;
    try {
      const decrypted = CryptoJS.AES.decrypt(encrypted, ENCRYPTION_KEY).toString(
        CryptoJS.enc.Utf8
      );
      return decrypted;
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string) => {
    const encrypted = CryptoJS.AES.encrypt(value, ENCRYPTION_KEY).toString();
    localStorage.setItem(name, encrypted);
  },
  removeItem: (name: string) => {
    localStorage.removeItem(name);
  },
};

interface PresenceManagerState {
  // PresenceManager instance (not persisted)
  manager: PresenceManager | null;

  // Connection state
  connectionState: ConnectionState;
  isConnected: boolean;

  // User presence data
  currentUserPresence: UserPresenceUpdate | null;
  onlineUsers: Map<string, UserPresenceUpdate>;

  // Automatic features state
  isIdle: boolean;
  lastHeartbeat: Date | null;
  lastActivity: Date | null;
  idleCountdown: number; // seconds remaining

  // Admin features (null if not admin)
  presenceStats: PresenceStats | null;
  isAdmin: boolean;

  // Configuration
  config: {
    autoHeartbeat: boolean;
    heartbeatInterval: number;
    activityTracking: boolean;
    activityDebounce: number;
    highSignificanceThrottle: number;
    lowSignificanceThrottle: number;
    idleDetection: boolean;
    idleTimeout: number;
    idleGracePeriod: number;
    autoLockOnIdle: boolean;
    lockWarningThreshold: number;
  };

  // Actions
  initialize: (hubUrl: string, getToken: () => Promise<string | null>) => void;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  updateStatus: (status: PresenceStatus, message?: string) => Promise<void>;
  refreshOnlineUsers: () => Promise<void>;
  refreshStats: () => Promise<void>;
  updateConfig: (config: Partial<PresenceManagerState['config']>) => void;
  setIdleCountdown: (seconds: number) => void;
  destroy: () => Promise<void>;
}

export const usePresenceManagerStore = create<PresenceManagerState>()(
  persist(
    (set, get) => ({
      // Initial state
      manager: null,
      connectionState: ConnectionState.Disconnected,
      isConnected: false,
      currentUserPresence: null,
      onlineUsers: new Map(),
      isIdle: false,
      lastHeartbeat: null,
      lastActivity: null,
      idleCountdown: 0,
      presenceStats: null,
      isAdmin: false,
      config: {
        autoHeartbeat: true,
        heartbeatInterval: 30,
        activityTracking: true,
        activityDebounce: 5000,
        highSignificanceThrottle: 2000,
        lowSignificanceThrottle: 5000,
        idleDetection: true,
        idleTimeout: 5,
        idleGracePeriod: 30,
        autoLockOnIdle: true,
        lockWarningThreshold: 20,
      },

      // Initialize PresenceManager
      initialize: (hubUrl, getToken) => {
        const { config } = get();

        const manager = new PresenceManager(hubUrl, getToken, {
          autoHeartbeat: config.autoHeartbeat,
          heartbeatInterval: config.heartbeatInterval,
          activityTracking: config.activityTracking,
          activityDebounce: config.activityDebounce,
          highSignificanceThrottle: config.highSignificanceThrottle,
          lowSignificanceThrottle: config.lowSignificanceThrottle,
          idleDetection: config.idleDetection,
          idleTimeout: config.idleTimeout,
          idleGracePeriod: config.idleGracePeriod,
          platform: 'web',
        });

        // Subscribe to connection state changes
        manager.client.onStateChange((state) => {
          set({
            connectionState: state,
            isConnected: state === ConnectionState.Connected,
          });
        });

        // Subscribe to presence events
        // SDK's PresenceClient emits local events with these names
        // (not the SignalR server event names like 'UserIsOnline')
        manager.on('userOnline', (user: UserPresenceUpdate) => {
          console.log('[PresenceManagerStore] userOnline event:', user);
          set((state) => {
            const newMap = new Map(state.onlineUsers);
            newMap.set(user.userId, user);
            return { onlineUsers: newMap };
          });
        });

        manager.on('userOffline', (user: UserPresenceUpdate) => {
          console.log('[PresenceManagerStore] userOffline event:', user);
          set((state) => {
            const newMap = new Map(state.onlineUsers);
            newMap.delete(user.userId);
            return { onlineUsers: newMap };
          });
        });

        manager.on('statusChanged', (user: UserPresenceUpdate) => {
          console.log('[PresenceManagerStore] statusChanged event:', user);
          set((state) => {
            const newMap = new Map(state.onlineUsers);
            newMap.set(user.userId, user);
            return { onlineUsers: newMap };
          });
        });

        // Subscribe to current user presence changes (handled by PresenceClient internally)
        manager.on('currentUserPresenceChanged', (presence: UserPresenceUpdate) => {
          console.log('[PresenceManagerStore] Current user presence changed:', presence);
          set({ currentUserPresence: presence });
        });

        // Subscribe to idle state changes
        manager.onIdleChange((idle) => {
          console.log('[PresenceManagerStore] Idle state changed:', idle);
          set({ isIdle: idle });
        });

        // Clear any existing countdown interval before creating a new one
        if (countdownIntervalId) {
          clearInterval(countdownIntervalId);
        }

        // Start idle countdown timer - updates every second for accurate countdown
        countdownIntervalId = setInterval(() => {
          const state = get();
          if (state.manager) {
            const remaining = state.manager.getRemainingIdleTime();
            const lastHeartbeat = state.manager.getLastHeartbeat();
            const lastActivity = state.manager.getLastActivity();
            set({
              idleCountdown: remaining,
              lastHeartbeat,
              lastActivity,
            });
          }
        }, 1000); // Update every second for countdown accuracy

        set({ manager });
      },

      // Connect to PresenceHub
      connect: async () => {
        const { manager } = get();
        if (!manager) {
          throw new Error('PresenceManager not initialized');
        }
        await manager.connect();

        // Load initial data
        await get().refreshOnlineUsers();

        // Check if user is admin and load stats
        try {
          const stats = await manager.getPresenceStats();
          set({ presenceStats: stats, isAdmin: true });
        } catch (error: any) {
          console.warn('[PresenceManager] Not an admin user or failed to load stats:', error?.message || error);
          set({ isAdmin: false, presenceStats: null });
        }
      },

      // Disconnect
      disconnect: async () => {
        const { manager } = get();
        if (manager) {
          await manager.disconnect();
        }
      },

      // Update status
      updateStatus: async (status, message) => {
        const { manager } = get();
        if (!manager) return;
        await manager.updateStatus(status, message);

        const currentPresence = manager.getCurrentUserPresence();
        set({ currentUserPresence: currentPresence });
      },

      // Refresh online users
      refreshOnlineUsers: async () => {
        const { manager } = get();
        if (!manager) return;

        const allPresence = await manager.getAllPresence();
        const onlineMap = new Map(
          allPresence
            .filter((u) => u.isOnline)
            .map((u) => [u.userId, u])
        );

        set({ onlineUsers: onlineMap });
      },

      // Refresh admin stats
      refreshStats: async () => {
        const { manager, isAdmin } = get();
        if (!manager || !isAdmin) return;

        try {
          const stats = await manager.getPresenceStats();
          set({ presenceStats: stats });
        } catch (error) {
          console.error('Failed to refresh stats:', error);
        }
      },

      // Update configuration
      updateConfig: (newConfig) => {
        const { manager, config } = get();
        const updatedConfig = { ...config, ...newConfig };

        // Update manager settings
        if (manager) {
          if (newConfig.autoHeartbeat !== undefined) {
            manager.setAutoHeartbeat(newConfig.autoHeartbeat);
          }
          if (newConfig.heartbeatInterval !== undefined) {
            manager.setHeartbeatInterval(newConfig.heartbeatInterval);
          }
          if (newConfig.activityTracking !== undefined) {
            manager.setActivityTracking(newConfig.activityTracking);
          }
          if (newConfig.activityDebounce !== undefined) {
            manager.setActivityDebounce(newConfig.activityDebounce);
          }
          if (newConfig.highSignificanceThrottle !== undefined) {
            manager.setHighSignificanceThrottle(newConfig.highSignificanceThrottle);
          }
          if (newConfig.lowSignificanceThrottle !== undefined) {
            manager.setLowSignificanceThrottle(newConfig.lowSignificanceThrottle);
          }
          if (newConfig.idleDetection !== undefined) {
            manager.setIdleDetection(newConfig.idleDetection);
          }
          if (newConfig.idleTimeout !== undefined) {
            manager.setIdleTimeout(newConfig.idleTimeout);
          }
          if (newConfig.idleGracePeriod !== undefined) {
            manager.setIdleGracePeriod(newConfig.idleGracePeriod);
          }
        }

        set({ config: updatedConfig });
      },

      // Set idle countdown (called by timer)
      setIdleCountdown: (seconds) => {
        set({ idleCountdown: seconds });
      },

      // Destroy manager
      destroy: async () => {
        // Clear countdown interval to prevent memory leak
        if (countdownIntervalId) {
          clearInterval(countdownIntervalId);
          countdownIntervalId = null;
        }

        const { manager } = get();
        if (manager) {
          await manager.destroy();
          set({ manager: null });
        }
      },
    }),
    {
      name: 'ccms-presence-manager',
      storage: createJSONStorage(() => encryptedStorage),
      partialize: (state) => ({
        // Only persist configuration, not runtime state
        config: state.config,
      }),
    }
  )
);
